import React from 'react';
import classnames from 'classnames';
import Select, {
  components,
  ControlProps,
  MenuProps,
  OptionProps,
  SingleValue as SingleValueType,
  SingleValueProps,
  ValueContainerProps
} from 'react-select';
import {AllowedLocale} from '../../../redux/localeActionTypes';
import usaFlag from '../../../assets/langs/en.png';
import isFlag from '../../../assets/langs/he.png';
import globe from '../../../assets/globe.png';
import globeMobile from '../../../assets/icons/globe-mobile.svg';
import styles from './SelectLocaleDropdown.module.scss';

interface LanguageOption {
  value: AllowedLocale;
  label: React.FC<LabelProps>;
}

export interface LocaleSelectorProps {
  current: AllowedLocale,
  onChange: (value: AllowedLocale) => void;
  inputId?: string,
}

export interface LabelProps {
  singleValue: boolean;
}

export const englishLabelText = 'English';
const EnglishLabel = ({singleValue}: LabelProps) => (
  <div className={styles.labelContainer}>
    <img src={usaFlag} alt="usa-flag" className={styles.icon}/>
    <div className={classnames(styles.text, {[styles.customText]: singleValue})}>{englishLabelText}</div>
  </div>
);

export const hebrewLabelText = 'עברית';
const HebrewLabel = ({singleValue}: LabelProps) => (
  <div className={styles.labelContainer}>
    <img src={isFlag} alt="israel-flag" className={styles.icon}/>
    <div className={classnames(styles.text, {[styles.customText]: singleValue})}>{hebrewLabelText}</div>
  </div>
);

export const locales: LanguageOption[] = [
  {value: 'he', label: HebrewLabel},
  {value: 'en', label: EnglishLabel},
];

const Control = ({children, ...props}: ControlProps<LanguageOption>) => {
  return (
    <components.Control
      {...props}
      className={classnames(props.className, styles.customControl)}
    >
      {children}
    </components.Control>
  );
};

const Option = (props: OptionProps<LanguageOption>) => {
  const Label = props.data.label;
  return (
    <components.Option
      {...props}
      className={classnames(props.className, styles.customOption)}
    >
      <Label singleValue={false}/>
    </components.Option>
  );
};

const Menu = ({children, ...props}: MenuProps<LanguageOption>) => {
  const isOutOfBottomViewportBorder = () => {
    if (typeof props.innerRef === 'object') {
      if (props.innerRef?.current) {
        const rect = props.innerRef.current.getBoundingClientRect();
        return window.innerHeight < (rect.y + props.maxMenuHeight);
      }
    }
    return false;
  }

  return (
    <components.Menu
      {...props}
      className={classnames(props.className, styles.customMenu, {
        [styles.customMenuPositionTop]: isOutOfBottomViewportBorder()
      })}
    >
      {children}
    </components.Menu>
  );
};

const SingleValue = ({
                       ...props
                     }: SingleValueProps<LanguageOption>) => {
  const value = props.data.value === 'he' ? 'עברית' : 'English';

  return (
    <components.SingleValue
      {...props}
      className={classnames(props.className, styles.customSingleValue)}
    >
      <div className={styles.labelContainer}>
        <div className={classnames(styles.text, styles.customText)}>{value}</div>
        <img src={globe} alt="globe" className={styles.iconDesktop}/>
        <img src={globeMobile} alt="globe" className={styles.iconMobile}/>
      </div>
    </components.SingleValue>
  );
};

const ValueContainer = ({
                          children,
                          ...props
                        }: ValueContainerProps<LanguageOption>) => {

  return (
    <components.ValueContainer
      {...props}
      className={classnames(props.className, styles.customValueContainer)}
    >
      {children}
    </components.ValueContainer>
  )
};


const SelectLocaleDropdown = ({
                                current,
                                onChange,
                                ...rest
                              }: LocaleSelectorProps) => {
  const [currentValue] = locales.filter((item: LanguageOption) => item.value === current);
  const handleChange = (newValue: SingleValueType<LanguageOption>) => {
    if (onChange && newValue) {
      onChange(newValue.value);
    }
  };

  return (
    <div className={styles.localeSelectorContainer}>
      <Select<LanguageOption>
        value={currentValue}
        onChange={handleChange}
        options={locales}
        components={{
          Control,
          Option,
          Menu,
          SingleValue,
          DropdownIndicator: null,
          ValueContainer
        }}
        isSearchable={false}
        {...rest}
      />
    </div>
  );
};

export default SelectLocaleDropdown;
